import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import EvidenceSnapshotView from "@/components/EvidenceSnapshotView";
import {
    ArrowLeft, AlertCircle, Calendar, Brain, ShieldCheck, FileText, Download, User as UserIcon, Mail,
} from "lucide-react";
import { EASING, DURATION } from "@/lib/motionSystem";
import { TIER_COLORS } from "@/lib/tiers";
import { cohortLabel } from "@/lib/format";


const SIGNAL_LABELS = {
    academic: "Academic",
    fee_payment: "Fee Payment",
    developer_professional: "Developer / Professional",
    social_creator: "Social / Creator",
    consumption: "Consumption",
    coverage_consistency: "Coverage & Consistency",
};

function formatDate(value) {
    if (!value) return "—";
    try {
        return new Date(value).toLocaleString("en-IN", { dateStyle: "long", timeStyle: "short", timeZone: "Asia/Kolkata" });
    } catch {
        return "—";
    }
}

function downloadJson(filename, data) {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

export default function AdminScoreDetail() {
    const { scoreId } = useParams();
    const [record, setRecord] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/api/admin/scores/${scoreId}`);
                if (cancelled) return;
                setRecord(data);
            } catch (err) {
                if (cancelled) return;
                setError(err.response?.data?.detail || "Couldn't load this score record.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [scoreId]);

    if (loading) {
        return (
            <div className="pt-24 flex justify-center min-h-dvh">
                <p className="text-slate-500">Loading score...</p>
            </div>
        );
    }

    if (error || !record) {
        return (
            <div className="pt-24 px-6 flex justify-center min-h-dvh">
                <div className="rounded-xl border border-rose-200 bg-white p-8 max-w-md text-center shadow-sm">
                    <AlertCircle size={32} className="text-rose-500 mx-auto mb-3" />
                    <h2 className="font-heading text-xl font-semibold text-slate-900 mb-2">Score not found</h2>
                    <p className="text-slate-500 text-sm mb-4">{error || "This score could not be loaded."}</p>
                    <Link to="/genz-credit-scoring/admin" className="text-blue-700 text-sm font-medium hover:underline">
                        Back to admin console
                    </Link>
                </div>
            </div>
        );
    }

    const tierColor = TIER_COLORS[record.tier] || "#334155";
    const explanation = record.ai_explanation || {};
    const persona = record.persona_match || {};
    const signalScores = record.signal_scores || {};
    const consentSnapshot = record.consent_snapshot || {};
    const consentEntries = Object.entries(consentSnapshot);

    return (
        <div className="pt-24 pb-16 px-6 md:px-12 lg:px-24 min-h-dvh" data-testid="admin-score-detail">
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: DURATION.pageTransition, ease: EASING.standard }}
                className="max-w-6xl mx-auto"
            >
                <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                    <Link
                        to={record.user?.id ? `/genz-credit-scoring/admin/users/${record.user.id}` : "/genz-credit-scoring/admin"}
                        className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-800 text-sm transition-colors"
                    >
                        <ArrowLeft size={14} /> {record.user?.id ? "Back to user" : "Back to admin"}
                    </Link>
                    <button
                        onClick={() => downloadJson(`score-${record.id}.json`, record)}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50 transition-all"
                    >
                        <Download size={14} /> Export JSON
                    </button>
                </div>

                <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="md:col-span-2">
                            <p className="text-slate-500 text-xs uppercase tracking-[0.3em] mb-2 inline-flex items-center gap-2">
                                <Calendar size={12} /> {formatDate(record.created_at)}
                            </p>
                            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
                                Score record · <span className="font-mono">{record.score}</span>
                            </h1>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-600 mb-4">
                                <span className="font-semibold px-3 py-0.5 rounded-full text-xs" style={{ background: `${tierColor}1a`, color: tierColor }}>
                                    {record.tier}
                                </span>
                                <span className="inline-flex items-center gap-1.5">
                                    <ShieldCheck size={13} /> {Math.round((record.confidence || 0) * 100)}% confidence
                                </span>
                                <span className="inline-flex items-center gap-1.5">
                                    <FileText size={13} /> {record.evidence_count ?? record.evidence_snapshot?.length ?? 0} evidence sources
                                </span>
                                {record.is_demo && (
                                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">DEMO</span>
                                )}
                            </div>

                            {record.user && (
                                <div className="border-t border-slate-100 pt-4">
                                    <p className="text-slate-500 text-xs mb-2">Submitted by</p>
                                    <Link
                                        to={`/genz-credit-scoring/admin/users/${record.user.id}`}
                                        className="inline-flex items-center gap-3 group"
                                    >
                                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                                            <UserIcon size={18} className="text-slate-500" />
                                        </div>
                                        <div>
                                            <p className="text-slate-900 text-sm font-semibold group-hover:underline">
                                                {record.user.name || "Unnamed user"}
                                            </p>
                                            <p className="text-slate-500 text-xs inline-flex items-center gap-1">
                                                <Mail size={11} /> {record.user.email}
                                            </p>
                                        </div>
                                    </Link>
                                </div>
                            )}
                        </div>

                        <div className="border-t md:border-t-0 md:border-l border-slate-100 md:pl-6 pt-4 md:pt-0">
                            <p className="text-slate-500 text-xs mb-3">Persona match</p>
                            <p className="text-slate-900 text-base font-medium mb-1">{cohortLabel(persona, "—")}</p>
                            <p className="text-slate-500 text-xs mb-3">
                                {persona.institution_tier && `${persona.institution_tier.replace(/_/g, " ")}`}
                                {persona.course_category && ` · ${persona.course_category.replace(/_/g, " ")}`}
                            </p>
                            {Array.isArray(persona.percentile_range) && (
                                <p className="text-slate-700 text-sm font-mono">
                                    Percentile: {persona.percentile_range[0]}–{persona.percentile_range[1]}
                                </p>
                            )}
                            <p className="text-slate-500 text-xs mt-2">
                                {Math.round((persona.confidence || 0) * 100)}% match confidence{persona.is_inferred ? " · inferred" : ""}
                            </p>
                        </div>
                    </div>
                </section>

                {(explanation.summary || explanation.lender_summary) && (
                    <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm mb-6">
                        <h3 className="font-heading text-lg font-medium text-slate-900 mb-3 flex items-center gap-2">
                            <Brain size={16} className="text-blue-600" /> AI Analysis
                        </h3>
                        {explanation.summary && (
                            <p className="text-slate-800 text-sm leading-relaxed mb-3">{explanation.summary}</p>
                        )}
                        {explanation.lender_summary && (
                            <div className="mt-3 pt-3 border-t border-slate-100">
                                <p className="text-slate-500 text-xs uppercase tracking-wider mb-1.5">Lender summary</p>
                                <p className="text-slate-700 text-sm leading-relaxed">{explanation.lender_summary}</p>
                            </div>
                        )}
                        {explanation.generated_by && (
                            <p className="text-slate-400 text-[11px] mt-3 font-mono">
                                Generated by: {explanation.generated_by}
                            </p>
                        )}
                    </section>
                )}

                {Object.keys(signalScores).length > 0 && (
                    <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm mb-6">
                        <h3 className="font-heading text-lg font-medium text-slate-900 mb-4">Signal Breakdown</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {Object.entries(signalScores).map(([key, val]) => {
                                const score = val?.score ?? 0;
                                const weight = (val?.weight ?? 0) * 100;
                                return (
                                    <div key={key} className="rounded-lg border border-slate-200 p-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="text-slate-900 text-sm font-semibold">{SIGNAL_LABELS[key] || key}</p>
                                            <p className="font-mono text-base font-bold text-slate-900">
                                                {Math.round(score)}<span className="text-xs text-slate-500">/100</span>
                                            </p>
                                        </div>
                                        <p className="text-slate-500 text-xs mb-2">Weight: {weight.toFixed(0)}%</p>
                                        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                                            <div
                                                className="h-full rounded-full"
                                                style={{
                                                    width: `${Math.max(0, Math.min(100, score))}%`,
                                                    background: score >= 70 ? "#16a34a" : score >= 50 ? "#ca8a04" : "#dc2626",
                                                }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                )}

                <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm mb-6">
                    <h3 className="font-heading text-lg font-medium text-slate-900 mb-1">
                        Evidence Snapshot
                    </h3>
                    <p className="text-slate-500 text-sm mb-5">
                        Exact evidence captured at the time this score was generated. Immutable — does not reflect later updates by the user.
                    </p>
                    <EvidenceSnapshotView
                        evidence={record.evidence_snapshot || []}
                        theme="light"
                        emptyMessage={record.snapshot_backfilled
                            ? "Snapshot was backfilled from current evidence — may not match the user's exact submission at the time."
                            : "This score predates evidence snapshotting. Run admin backfill or wait for the user to generate a new score."}
                    />
                </section>

                {consentEntries.length > 0 && (
                    <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                        <h3 className="font-heading text-lg font-medium text-slate-900 mb-1">Consent State at Generation</h3>
                        <p className="text-slate-500 text-sm mb-5">
                            Snapshot of the user's consent grants at the moment this score was computed.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                            {consentEntries.map(([scope, scopeData]) => {
                                const granted = scopeData?.granted;
                                return (
                                    <div key={scope} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-slate-50">
                                        <div className="min-w-0">
                                            <p className="text-slate-900 text-sm font-medium capitalize">{scope.replace(/_/g, " ")}</p>
                                            {scopeData?.timestamp && (
                                                <p className="text-slate-500 text-xs">Set {formatDate(scopeData.timestamp)}</p>
                                            )}
                                        </div>
                                        <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-full shrink-0 ${
                                            granted ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
                                        }`}>
                                            {granted ? "Granted" : "Revoked"}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                )}
            </motion.div>
        </div>
    );
}
